import React, { FC } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import CourseIcon from '~/assets/icons/CourseIcon';
import PlayLargeIcon from '~/assets/icons/PlayLargeIcon';
import CardImageLabel from '~/components/Card/components/CardImageLabel/CardImageLabel';
import PathwayLogo from '~/components/TestResults/components/PathwayLogo';
import ZoomedBackground, { createCustomDimensionsImgWrapper } from '~/components/ZoomedBackground';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { ComingSoonOverlay } from '~/pages/DiscoverPage/components/ComingSoonOverlay/ComingSoonOverlay';
import GlossaryGridHit from '~/pages/DiscoverPage/components/GlossaryGridHit/GlossaryGridHit';
import PathwayStatus from '~/pages/DiscoverPage/components/GridHit/PathwayStatus';
import { getCategoriesArray, getGeneralizedObject } from '~/pages/DiscoverPage/components/Hit/Hit';
import { HITS_TYPES, PATHWAY_STATUSES, PUBLISH_STATUSES } from '~/pages/const';
import { FALLBACK_LINK } from '~/routes';
import { css } from '~/styled';
import { getDefaultTransition, SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';
import { DiscoverPathwaysDetailedInfo } from '~/types';

interface GridHitProps {
  hit?: any;
  onClick: () => void;
  pathwayProgress?: DiscoverPathwaysDetailedInfo | null;
}

function calcPathwayStatus({ hasUncompletedQuizes, watchedVideos, totalVideos }): PATHWAY_STATUSES {
  const started = !!watchedVideos;
  const notStarted = !started;

  if (notStarted) {
    return PATHWAY_STATUSES.NOT_WATCHED;
  }

  const watched = started && watchedVideos === totalVideos;

  const status = watched
    ? hasUncompletedQuizes
      ? PATHWAY_STATUSES.WATCHED
      : PATHWAY_STATUSES.COMPLETED
    : PATHWAY_STATUSES.STARTED;

  return status;
}

const GridHit: FC<GridHitProps> = ({ hit, onClick, pathwayProgress }) => {
  const theme = useTheme();
  const { t, i18n } = useTenantTranslation();
  const isSsku = useIsSsku();
  const data = getGeneralizedObject({
    hit,
    t,
    videoProgress: undefined,
    locale: i18n.language,
    isSsku,
  });
  const { tenant } = useTenantContext();
  const { suiteIDs } = useUserInfoContext();

  const tenantIDs = typeof tenant?.id === 'string' ? [tenant.id] : [];

  const categories = getCategoriesArray({
    item: hit,
    isGridHit: false,
    locale: i18n.language,
    availableIDs: suiteIDs.length > 0 ? suiteIDs : tenantIDs,
  });

  const searchableType = hit?.searchable_type?.toString();
  if (searchableType === HITS_TYPES.GLOSSARY) {
    return <GlossaryGridHit hit={hit} onClick={onClick} />;
  }

  const watchedVideos = pathwayProgress?.count_watched_videos || 0;
  const totalVideos = data?.count_videos || 0;
  const comingSoon = data?.publish_status === PUBLISH_STATUSES.ComingSoon;

  const status = calcPathwayStatus({
    hasUncompletedQuizes: Boolean(pathwayProgress?.not_passed_quizes?.length),
    watchedVideos,
    totalVideos,
  });

  let statusTooltip;

  switch (status) {
    case PATHWAY_STATUSES.STARTED:
      statusTooltip = `${Math.round((watchedVideos * 100) / totalVideos)} % ${t(
        'common.watched',
      ).toLowerCase()}`;
      break;
    case PATHWAY_STATUSES.WATCHED:
      statusTooltip = t('page.discover.takeATest');
      break;
    case PATHWAY_STATUSES.COMPLETED:
      statusTooltip = t('common.completed');
  }

  return (
    <Wrapper
      path={data?.preview_url}
      height={60}
      imgWrapperCss={createCustomDimensionsImgWrapper({ height: '60%' })}
    >
      {status !== PATHWAY_STATUSES.NOT_WATCHED && (
        <StyledPathwayStatus status={status} tooltip={statusTooltip} />
      )}
      {searchableType === HITS_TYPES.COURSE && (
        <StyledPathwayLogo
          circleUrl={tenant?.circle_image_url}
          icon={
            data?.svg_icon || (
              <CourseIcon secondaryColor={alpha(theme.palette.common.white, 0.33)} />
            )
          }
        />
      )}
      <StyledLink to={data?.link || FALLBACK_LINK} onClick={onClick} comingSoon={comingSoon}>
        {searchableType === HITS_TYPES.VIDEO && (
          <PlayIconWrap>
            <PlayIcon color={theme.palette.common.white} />
          </PlayIconWrap>
        )}
        {searchableType === HITS_TYPES.PATHWAY && (
          <StyledCardImageLabel
            name={t('page.discover.pathway')}
            bgColor={theme.palette.common.pink}
            textColor={theme.palette.common.category.color}
          />
        )}
        {searchableType === HITS_TYPES.NEWS && (
          <StyledCardImageLabel
            name={t('page.discover.insight')}
            bgColor={theme.palette.common.blueLight}
            textColor={theme.palette.common.white}
          />
        )}
        {comingSoon && <ComingSoonOverlay />}
      </StyledLink>
      <InfoWrap>
        <LabelsWrapper>
          {categories && categories.length > 0 && (
            <CategoriesWrapper>{categories}</CategoriesWrapper>
          )}
        </LabelsWrapper>
        <StyledTitleLink to={data?.link || ''} onClick={onClick} comingSoon={comingSoon}>
          <Title isCourse={searchableType === HITS_TYPES.COURSE} variant="h2">
            {data?.title}
          </Title>
        </StyledTitleLink>
        <Description>{data?.accompanyingText}</Description>
      </InfoWrap>
    </Wrapper>
  );
};

export default GridHit;

const StyledTitleLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'comingSoon',
})<{ comingSoon: boolean }>`
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};
`;
const StyledPathwayStatus = styled(PathwayStatus)`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  padding: 12px;
  box-sizing: content-box;
`;
const StyledCardImageLabel = styled(CardImageLabel)`
  position: absolute;
  top: 12px;
  left: 12px;
`;
const Wrapper = styled(ZoomedBackground)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 338px;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: ${SMALL_MARGIN_PX};
  background: ${({ theme }) => theme.palette.common.blockBackground.light};
  position: relative;
  transition: ${getDefaultTransition('background-color')};

  &:hover {
    & > a {
      &:before {
        opacity: 1;
      }
    }
    background-color: ${({ theme }) => theme.palette.common.card.hover};
  }
`;
const backgroundShadowCss = css`
  content: '';
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: 90%;
  -webkit-background-origin: padding-box;
  -o-background-origin: padding-box;
  -moz-background-origin: padding-box;
`;
const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'comingSoon',
})<{ comingSoon: boolean }>`
  width: 100%;
  height: 208px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};

  &::after {
    ${backgroundShadowCss};
    bottom: ${({ comingSoon }) => (comingSoon ? 0 : '5px')};
    background: linear-gradient(
        0deg,
        ${({ theme }) => theme.palette.common.blockBackground.light} 0%,
        ${({ theme }) => alpha(theme.palette.common.blockBackground.light, 0.9)} 17%,
        ${({ theme }) => alpha(theme.palette.common.blockBackground.light, 0)} 100%
      )
      0% 0% no-repeat;
  }
  &::before {
    ${backgroundShadowCss};
    bottom: ${({ comingSoon }) => (comingSoon ? 0 : '5px')};
    transition: ${getDefaultTransition('opacity')};
    opacity: 0;
    z-index: 1;
    background: ${({ theme }) => theme.palette.common.card.hoverGradient};
  }
`;
const PlayIconWrap = styled(Box)`
  position: absolute;
  width: 64px;
  height: 64px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  border-radius: 50%;
  background: ${({ theme }) => alpha(theme.palette.common.blueDark, 0.5)};
`;
const PlayIcon = styled(PlayLargeIcon)`
  width: 28px;
  height: 32px;
  margin-left: 6px;
`;
const InfoWrap = styled(Box)`
  position: relative;
  padding: 0 ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
`;
const LabelsWrapper = styled('div')`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  z-index: 1;
  & .cpdLabel {
    padding: 4.5px 9px;
  }
`;
const CategoriesWrapper = styled(Box)`
  display: inline-flex;
  flex-wrap: wrap;

  & > a:not(:last-child) {
    margin-right: 9px;
    margin-bottom: 9px;
  }

  & > div {
    margin-right: 9px;
    margin-bottom: 9px;

    &:last-child {
      margin-right: 0;
    }
  }

  &:not(:only-child) {
    margin-right: 10px;
  }
`;
const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCourse',
})<{ isCourse: boolean }>`
  && {
    font-size: 16px;
    line-height: 1.2;
    font-weight: bold;
    margin-bottom: 9px;
    color: ${({ theme }) => theme.palette.common.white};
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: ${({ isCourse }) => (isCourse ? 3 : 2)};
    -webkit-box-orient: vertical;
    word-break: break-word;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
    }
  }
`;
const Description = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
  font-weight: normal;
  color: ${({ theme }) => theme.palette.common.text.meta};
`;
const StyledPathwayLogo = styled(PathwayLogo)`
  && {
    position: absolute;
    top: 12px;
    left: 12px;
    width: 40px;
    height: 40px;
    z-index: 2;
  }

  & svg {
    filter: invert(${({ theme }) => (theme.palette.mode === 'light' ? 1 : 0)});
  }
`;
